import React, { memo, useEffect, useState } from 'react'

import { Icon, InfoOutlined, Tooltip } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  node, object, oneOfType, string,
} from 'prop-types'

import DesktopOnlyTooltip from 'Pods/Shared/DesktopOnlyTooltip/DesktopOnlyTooltip'

import useBreakpoint from 'Utils/useBreakpoint'

// Long enough that the card does not flash while the pointer crosses the icon on
// its way somewhere else, short enough to still read as a hover affordance.
const HOVER_DELAY = 300

// Info icon whose card opens on hover and keyboard focus from `sm` up (>= 640px)
// and on tap below it, where there is no hover to trigger.
//
// Two components rather than one flag: react-tooltip binds its listeners only in
// componentDidMount (and on ReactTooltip.rebuild), so flipping `data-event` on a
// mounted instance leaves the old hover listeners in place. Swapping the component
// type across the breakpoint remounts react-tooltip, which is what makes the tap
// listener actually bind. DesktopOnlyTooltip also owns the only keyboard path -
// Triangle's Tooltip has none.
function InfoIconTooltip({
  id,
  content,
  label,
  place = 'top',
  tooltipClassName = '',
  triggerClassName = '',
  iconSize = 'small',
}) {
  const isTouch = useBreakpoint() === 'xs'
  const [hasHydrated, setHasHydrated] = useState(false)

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  const trigger = (
    <button type="button" className={triggerClassName} aria-label={label}>
      <Icon svgComponent={InfoOutlined} color="darker" size={iconSize} notSpaced />
    </button>
  )

  // `/ai` is a fast page and useBreakpoint is undefined during SSR, so the server
  // always emits the hover variant. The hook reports the real width on the first
  // client render, so branching on it there would swap the element tree
  // mid-hydration; match the server markup first, then switch on the next paint.
  if (hasHydrated && isTouch) {
    return (
      <Tooltip
        id={id}
        content={content}
        tooltipClassName={tooltipClassName}
        event="click"
        place={place}
      >
        {trigger}
      </Tooltip>
    )
  }

  return (
    <DesktopOnlyTooltip
      id={id}
      content={content}
      tooltipClassName={tooltipClassName}
      place={place}
      delayShow={HOVER_DELAY}
      focusWithin
    >
      {trigger}
    </DesktopOnlyTooltip>
  )
}

InfoIconTooltip.propTypes = {
  id: string.isRequired,
  content: node.isRequired,
  label: string.isRequired,
  place: string,
  tooltipClassName: string,
  triggerClassName: string,
  iconSize: oneOfType([string, object]),
}

export default memo(InfoIconTooltip)
